import axios from 'axios';
import Cookies from 'js-cookie';
import { API_URLS, TIMEOUTS } from '../config/apiConfig';

// Admin client — uses admin JWT from localStorage (same as api.js)
const client = axios.create({
  baseURL: API_URLS.BACKEND_BASE,
  timeout: TIMEOUTS.DEFAULT,
  headers: { 'Content-Type': 'application/json' },
});

client.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('access_token') || Cookies.get('access_token');
    if (token) config.headers.Authorization = `Bearer ${token}`;
    return config;
  },
  (error) => Promise.reject(error),
);

// Merchant client — uses merchant_access_token cookie ONLY.
// Never falls back to localStorage admin token so that testing with both
// panels open in the same browser doesn't cross-contaminate.
const merchantClient = axios.create({
  baseURL: API_URLS.BACKEND_BASE,
  timeout: TIMEOUTS.DEFAULT,
  headers: { 'Content-Type': 'application/json' },
});

merchantClient.interceptors.request.use(
  (config) => {
    const token = Cookies.get('merchant_access_token') || Cookies.get('access_token');
    if (token) config.headers.Authorization = `Bearer ${token}`;
    return config;
  },
  (error) => Promise.reject(error),
);

merchantClient.interceptors.response.use(
  (response) => response,
  async (error) => {
    if (error.response && error.response.status === 429) {
      let data = error.response.data || {};
      if (data instanceof Blob) {
        try { data = JSON.parse(await data.text()); } catch (_) { data = {}; }
      }
      if (data.error === 'UsageLimitExceeded') {
        window.dispatchEvent(new CustomEvent('usage_limit_exceeded', {
          detail: {
            message: data.message || 'You have reached your usage limit for this billing period.',
            limit: data.limit,
            used: data.used,
          },
        }));
      }
    }
    return Promise.reject(error);
  },
);

const ADMIN  = '/api/v2/admin/subscription';
const MERCH  = '/api/v2/merchant/subscription';

// ─── Master Data ──────────────────────────────────────────────────────────────
export const getCurrencies        = ()        => client.get(`${ADMIN}/currencies`);
export const createCurrency       = (data)    => client.post(`${ADMIN}/currencies`, data);
export const updateCurrency       = (id, data)=> client.put(`${ADMIN}/currencies/${id}`, data);
export const deleteCurrency       = (id)      => client.delete(`${ADMIN}/currencies/${id}`);

export const getTaxRules          = ()        => client.get(`${ADMIN}/tax-rules`);
export const createTaxRule        = (data)    => client.post(`${ADMIN}/tax-rules`, data);
export const updateTaxRule        = (id, data)=> client.put(`${ADMIN}/tax-rules/${id}`, data);
export const deleteTaxRule        = (id)      => client.delete(`${ADMIN}/tax-rules/${id}`);

// ─── Plans & Add-ons ──────────────────────────────────────────────────────────
export const getMerchantModules   = ()        => client.get(`${ADMIN}/merchant-modules`);

export const getPlans             = (params)  => client.get(`${ADMIN}/plans`, { params });
export const getPlan              = (id)      => client.get(`${ADMIN}/plans/${id}`);
export const createPlan           = (data)    => client.post(`${ADMIN}/plans`, data);
export const updatePlan           = (id, data)=> client.put(`${ADMIN}/plans/${id}`, data);
export const publishPlan          = (id, data)=> client.post(`${ADMIN}/plans/${id}/publish`, data);
export const archivePlan          = (id)      => client.delete(`${ADMIN}/plans/${id}`);
export const duplicatePlan        = (id)      => client.post(`${ADMIN}/plans/${id}/duplicate`);
export const updatePlanVisibility = (id, data)=> client.put(`${ADMIN}/plans/${id}/visibility`, data);
export const ensurePlanDraft      = (id)      => client.post(`${ADMIN}/plans/${id}/ensure-draft`);
export const savePlanPricing      = (id, data)=> client.put(`${ADMIN}/plans/${id}/pricing`, data);
export const savePlanRbac         = (id, data)=> client.put(`${ADMIN}/plans/${id}/rbac`, data);
export const getPlanFeatureLabels  = (id)      => client.get(`${ADMIN}/plans/${id}/feature-labels`);
export const savePlanFeatureLabels = (id, data) => client.put(`${ADMIN}/plans/${id}/feature-labels`, data);
export const getAddonFeatureLabels = (id)       => client.get(`${ADMIN}/addons/${id}/feature-labels`);
export const saveAddonFeatureLabels= (id, data) => client.put(`${ADMIN}/addons/${id}/feature-labels`, data);

export const getAddons            = (params)  => client.get(`${ADMIN}/addons`, { params });
export const getAddon             = (id)      => client.get(`${ADMIN}/addons/${id}`);
export const createAddon          = (data)    => client.post(`${ADMIN}/addons`, data);
export const updateAddon          = (id, data)=> client.put(`${ADMIN}/addons/${id}`, data);
export const archiveAddon         = (id)      => client.delete(`${ADMIN}/addons/${id}`);
export const duplicateAddon       = (id)      => client.post(`${ADMIN}/addons/${id}/duplicate`);
export const updateAddonVisibility= (id, data)=> client.put(`${ADMIN}/addons/${id}/visibility`, data);

export const searchMerchants      = (q)       => client.get(`${ADMIN}/merchants`, { params: { search: q, per_page: 20 } });
export const lookupMerchant       = (params)  => client.get(`${ADMIN}/merchants/lookup`, { params });
export const bulkLookupMerchants  = (rows)    => client.post(`${ADMIN}/merchants/bulk-lookup`, { rows });
export const uploadPaymentAttachment = (file) => {
  const fd = new FormData(); fd.append('file', file);
  return client.post(`${ADMIN}/subscriptions/payment-attachments`, fd, {
    headers: { 'Content-Type': 'multipart/form-data' },
  });
};

// ─── AI generation reference data (from existing middleware-data routes) ──────
const MD = '/api/admin/middleware-data';
export const getCategories    = () => client.get(`${MD}/categories`);
export const getModelTypes    = () => client.get(`${MD}/model-types`);
export const getOutputFormats = () => client.get(`${MD}/output-formats`);
export const getMediaQualities= () => client.get(`${MD}/media-qualities`);
export const getEthnicities    = () => client.get(`${MD}/ethnicities`);
export const getClothingTypes  = () => client.get(`${MD}/clothing-types`);
export const getBustFinishes   = () => client.get(`${MD}/bust-finishes`);
export const getBustColors     = () => client.get(`${MD}/bust-colors`);

// ─── Subscriptions ────────────────────────────────────────────────────────────
export const getSubscriptions            = (params)  => client.get(`${ADMIN}/subscriptions`, { params });
export const getSubscription             = (id)      => client.get(`${ADMIN}/subscriptions/${id}`);
export const getSubscriptionLedger       = (id)      => client.get(`${ADMIN}/subscriptions/${id}/ledger`);
export const getAuditLogs                = (params)  => client.get(`${ADMIN}/audit-logs`, { params });
export const getSubscriptionPlanVersions = ()        => client.get(`${ADMIN}/subscriptions/plans`);
export const assignSubscription          = (data)    => client.post(`${ADMIN}/subscriptions/assign`, data);
export const assignAddonSubscription     = (data)    => client.post(`${ADMIN}/subscriptions/assign-addon`, data);
export const getAddonsForAssign          = ()        => client.get(`${ADMIN}/addons`, { params: { per_page: 200, sort_by: 'sort_order', sort_dir: 'asc' } });
export const getAddonsForMerchant        = (userId)  => client.get(`${ADMIN}/subscriptions/addons-for-assign`, { params: { user_id: userId } });
export const suspendSubscription         = (id)      => client.put(`${ADMIN}/subscriptions/${id}/suspend`);
export const reactivateSubscription      = (id)      => client.put(`${ADMIN}/subscriptions/${id}/reactivate`);
export const cancelSubscription          = (id, data)=> client.put(`${ADMIN}/subscriptions/${id}/cancel`, data);
export const extendSubscription          = (id, data)=> client.put(`${ADMIN}/subscriptions/${id}/extend`, data);
export const upgradeSubscription         = (id, data)=> client.put(`${ADMIN}/subscriptions/${id}/upgrade`, data);
export const recordPayment               = (id, data)=> client.post(`${ADMIN}/subscriptions/${id}/payment`, data);

// ─── Invoices ────────────────────────────────────────────────────────────────
export const getInvoices          = (params)  => client.get(`${ADMIN}/invoices`, { params });
export const getInvoice           = (id)      => client.get(`${ADMIN}/invoices/${id}`);
export const issueInvoice         = (id)      => client.post(`${ADMIN}/invoices/${id}/issue`);
export const markPaymentReceived  = (id, data)=> client.put(`${ADMIN}/invoices/${id}/mark-received`, data || {});
export const voidInvoice          = (id, data)=> client.put(`${ADMIN}/invoices/${id}/void`, data);
export const refundInvoice        = (id, data)=> client.post(`${ADMIN}/invoices/${id}/refund`, data);
export const sendPaymentLink      = (id, body = {}) => client.post(`${ADMIN}/invoices/${id}/send-payment-link`, body);
export const syncPaymentLinkStatus  = (id)     => client.post(`${ADMIN}/invoices/${id}/sync-payment-status`);
export const cancelPaymentLink      = (id)     => client.post(`${ADMIN}/invoices/${id}/cancel-payment-link`);

// ─── Coupons ─────────────────────────────────────────────────────────────────
export const getCoupons      = (params)  => client.get(`${ADMIN}/coupons`, { params });
export const getCoupon       = (id)      => client.get(`${ADMIN}/coupons/${id}`);
export const createCoupon    = (data)    => client.post(`${ADMIN}/coupons`, data);
export const updateCoupon    = (id, data)=> client.put(`${ADMIN}/coupons/${id}`, data);
export const activateCoupon    = (id)    => client.put(`${ADMIN}/coupons/${id}/activate`);
export const deactivateCoupon  = (id)    => client.put(`${ADMIN}/coupons/${id}/deactivate`);
export const getCouponUsage    = (id)    => client.get(`${ADMIN}/coupons/${id}/usage`);
export const previewCoupon   = (data)    => client.post(`${ADMIN}/coupons/preview`, data);

// ─── Content Pricing ─────────────────────────────────────────────────────────
export const getContentPricing      = ()        => client.get(`${ADMIN}/content-pricing`);
export const createContentPricing   = (data)    => client.post(`${ADMIN}/content-pricing`, data);
export const updateContentPricing   = (id, data)=> client.put(`${ADMIN}/content-pricing/${id}`, data);
export const createPricingOverride  = (data)    => client.post(`${ADMIN}/content-pricing/overrides`, data);
export const updatePricingOverride  = (id, data)=> client.put(`${ADMIN}/content-pricing/overrides/${id}`, data);
export const deletePricingOverride  = (id)      => client.delete(`${ADMIN}/content-pricing/overrides/${id}`);

// ─── Exports (blob response — triggers file download) ────────────────────────
export const exportPlans          = (params) => client.get(`${ADMIN}/plans/export`,          { params, responseType: 'blob' });
export const exportSubscriptions  = (params) => client.get(`${ADMIN}/subscriptions/export`,  { params, responseType: 'blob' });
export const exportInvoices       = (params) => client.get(`${ADMIN}/invoices/export`,        { params, responseType: 'blob' });
export const exportCoupons        = (params) => client.get(`${ADMIN}/coupons/export`,         { params, responseType: 'blob' });
export const exportContentPricing = ()       => client.get(`${ADMIN}/content-pricing/export`, { responseType: 'blob' });

// ─── Merchant-facing (merchantClient — never uses admin localStorage token) ───
export const getMerchantSubscription = ()     => merchantClient.get(`${MERCH}`);
export const getMerchantTokenBalance = ()     => merchantClient.get(`${MERCH}/tokens`);
export const getMerchantPlans        = ()     => merchantClient.get(`${MERCH}/plans`);
export const getMerchantAddons       = ()     => merchantClient.get(`${MERCH}/addons`);
export const checkoutPlan            = (data) => merchantClient.post(`${MERCH}/checkout`, data);
export const checkoutTopup           = (data) => merchantClient.post(`${MERCH}/topup`, data);
// Razorpay — call before opening modal; active only when subscription_razorpay_enabled=true
export const createCheckoutOrder     = (data) => merchantClient.post(`${MERCH}/checkout/create-order`, data);
export const createTopupOrder        = (data) => merchantClient.post(`${MERCH}/topup/create-order`, data);
export const cancelAutopay              = (addonId) => merchantClient.post(`${MERCH}/autopay/cancel`, addonId ? { addon_id: addonId } : {});
export const validateMerchantCoupon     = (data) => merchantClient.post(`${MERCH}/coupons/validate`, data);
export const getCheckoutAddressPrefill  = ()     => merchantClient.get(`${MERCH}/checkout/address-prefill`);
